/* CUSTOM CODE for RUGETTE -- START */
import { Component } from '@theme/component';

/**
 * @typedef {object} TabsRefs
 * @property {HTMLElement} tabList - The tablist container (buttons are generated into this)
 * @property {HTMLElement[]} tabsPanel - The tab panels
 */

/**
 * A custom element that powers a tabbed content layout. Builds the tablist of
 * `role="tab"` buttons from the panels' `data-tab-label` attributes, so the
 * tablist and the panels are genuinely separate regions in the DOM (matching
 * the WAI-ARIA APG tabs pattern), rather than interleaved per tab.
 *
 * Renders the same tab/panel layout on mobile and desktop (no accordion fallback).
 *
 * @extends {Component<TabsRefs>}
 */
class TabsComponent extends Component {
  requiredRefs = ['tabList', 'tabsPanel'];

  /** @type {HTMLButtonElement[]} */
  #triggers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#buildTabList();
    this.#activateDefaultTab();
  }

  #buildTabList() {
    const { tabList, tabsPanel } = this.refs;

    // No panels to power means there's nothing to make a tab, so leave the
    // list as a plain, role-less container rather than an empty tablist.
    if (!tabsPanel.length) return;

    tabList.setAttribute('role', 'tablist');
    tabList.setAttribute('aria-orientation', 'horizontal');
    tabList.addEventListener('keydown', this.#handleKeyDown);

    this.#triggers = tabsPanel.map(panel => {
      const trigger = document.createElement('button');

      trigger.type = 'button';
      trigger.className = 'tabs__trigger';
      trigger.id = `Tab-${panel.id}`;
      trigger.setAttribute('role', 'tab');
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-selected', 'false');
      trigger.tabIndex = -1;
      trigger.textContent = panel.dataset.tabLabel ?? '';
      trigger.addEventListener('click', () => this.#activate(trigger));

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', trigger.id);
      tabList.append(trigger);

      return trigger;
    });
  }

  #activateDefaultTab() {
    if (!this.#triggers.length) return;

    const { tabsPanel } = this.refs;
    const defaultPanelIndex = tabsPanel.findIndex(panel => panel.hasAttribute('data-default-selected'));

    this.#activate(this.#triggers[defaultPanelIndex] ?? this.#triggers[0]);
  }

  /**
   * Moves focus (and activates) between tabs using the arrow/home/end keys.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleKeyDown = event => {
    const currentIndex = this.#triggers.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));

    if (currentIndex === -1) return;

    let newIndex;

    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % this.#triggers.length;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + this.#triggers.length) % this.#triggers.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.#triggers.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTrigger = this.#triggers[newIndex];
    if (!nextTrigger) return;

    this.#activate(nextTrigger);
    nextTrigger.focus();
  };

  /**
   * @param {HTMLButtonElement | undefined} trigger - The trigger to activate.
   */
  #activate(trigger) {
    if (!trigger) return;

    const { tabsPanel } = this.refs;
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? tabsPanel.find(candidate => candidate.id === panelId) : undefined;

    for (const otherTrigger of this.#triggers) {
      const isActive = otherTrigger === trigger;
      otherTrigger.setAttribute('aria-selected', String(isActive));
      otherTrigger.tabIndex = isActive ? 0 : -1;
    }

    for (const otherPanel of tabsPanel) {
      otherPanel.hidden = otherPanel !== panel;
    }
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
/* CUSTOM CODE for RUGETTE -- END */
